import { Link } from 'react-router-dom';
import { useLang } from '../lib/lang';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import CountdownTimer from './CountdownTimer';
import InlineEditable from './InlineEditable';
import { Ticket, ArrowUpRight, Flame, Ban, Users } from 'lucide-react';

export default function RaffleCard({ raffle, showTickets = true, pointRate = 1, featured = false }) {
  const { t } = useLang();
  const realSold = raffle.sold_tickets || 0;
  const max = raffle.max_tickets || 1;
  // Use displayed override if set (psychological boost)
  const displayedSold = (raffle.displayed_sold_tickets !== null && raffle.displayed_sold_tickets !== undefined)
    ? raffle.displayed_sold_tickets
    : realSold;
  const isSoldOut = (raffle.force_sold_out === true) || (realSold >= max);
  const isComingSoon = raffle.status === 'coming_soon';
  // When sold out, admin can override draw date for an "advanced" earlier draw
  const hasAdvancedDrawDate = isSoldOut && !!raffle.draw_date;

  // For display purposes, when sold out, show full
  const sold = isSoldOut ? max : displayedSold;
  const pct = Math.round((sold / max) * 100);
  const remaining = isSoldOut ? 0 : Math.max(0, max - displayedSold);
  const isHot = pct > 60;
  const isEndingSoon = raffle.ends_at && new Date(raffle.ends_at) - new Date() < 86400000 * 2;
  const priceGBP = (raffle.ticket_cost_points / (pointRate || 1)).toFixed(0);

  return (
    <Link to={`/raffle/${raffle._id}`} data-testid="raffle-card" className="block group">
      <div className="draw-card-premium relative overflow-hidden">
        {/* Image area */}
        <div className="relative aspect-[16/13] overflow-hidden">
          <img
            src={raffle.images?.[0] || '/placeholder.jpg'}
            alt={raffle.title}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/45 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-[#0B3D2E]/40" />

          {/* SOLD OUT diagonal stripe */}
          {isSoldOut && (
            <div className="absolute inset-0 z-20 pointer-events-none overflow-hidden" data-testid="sold-out-stripe">
              {/* Dim overlay over image */}
              <div className="absolute inset-0 bg-black/55" />
              {/* Diagonal red ribbon — full edge-to-edge with rotation */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div
                  className="absolute left-[-15%] right-[-15%] py-3 sm:py-4 bg-gradient-to-r from-red-800/95 via-red-600 to-red-800/95 text-white font-display font-extrabold text-center uppercase tracking-[0.3em] shadow-[0_8px_32px_rgba(0,0,0,0.7)] backdrop-blur-sm"
                  style={{
                    transform: 'rotate(-12deg)',
                    boxShadow: '0 6px 24px rgba(220,38,38,0.45), inset 0 1px 0 rgba(255,255,255,0.25), inset 0 -1px 0 rgba(0,0,0,0.4)',
                  }}
                >
                  <span className="text-base sm:text-xl drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)] flex items-center justify-center gap-3">
                    <Ban className="w-5 h-5 sm:w-6 sm:h-6" strokeWidth={3} />
                    <span><InlineEditable tKey="soldOutBig">{t('soldOutBig') || 'All Tickets Sold'}</InlineEditable></span>
                    <Ban className="w-5 h-5 sm:w-6 sm:h-6" strokeWidth={3} />
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="absolute top-4 left-4 flex gap-2 z-10">
            {isComingSoon ? (
              <Badge className="bg-amber-500/95 backdrop-blur-sm text-white border-none text-[10px] font-bold tracking-[0.15em] px-3 py-1 uppercase flex items-center gap-1">
                ⏳ <InlineEditable tKey="comingSoon">{t('comingSoon') || 'Bientôt disponible'}</InlineEditable>
              </Badge>
            ) : isSoldOut ? (
              <Badge className="bg-red-500/95 backdrop-blur-sm text-white border-none text-[10px] font-bold tracking-[0.15em] px-3 py-1 uppercase">
                SOLD OUT
              </Badge>
            ) : (
              <>
                {isHot && (
                  <Badge className="bg-red-500/95 backdrop-blur-sm text-white border-none text-[10px] font-bold tracking-[0.15em] px-3 py-1 uppercase flex items-center gap-1">
                    <Flame className="w-3 h-3" /> <InlineEditable tKey="hot">{t('hot')}</InlineEditable>
                  </Badge>
                )}
                {isEndingSoon && (
                  <Badge className="bg-gold-gradient text-[#0B3D2E] border-none text-[10px] font-bold tracking-[0.15em] px-3 py-1 uppercase animate-pulse-gold">
                    <InlineEditable tKey="endingSoon">{t('endingSoon')}</InlineEditable>
                  </Badge>
                )}
              </>
            )}
          </div>

          {/* Value badge — hidden for coming_soon */}
          {!isComingSoon && (
            <div className="absolute top-4 right-4 z-10">
              <div className="bg-black/70 backdrop-blur-md rounded-lg px-3 py-2 border border-[hsl(var(--gold)/0.4)]">
                <p className="text-[8.5px] text-gold/95 uppercase tracking-[0.22em] font-bold leading-none"><InlineEditable tKey="value">{t('value')}</InlineEditable></p>
                <p className="font-mono text-base font-bold text-cream leading-tight mt-0.5">£{raffle.product_value?.toLocaleString()}</p>
              </div>
            </div>
          )}

          <div className="absolute bottom-0 left-0 right-0 p-5 z-10">
            <p className="text-[10px] font-bold text-gold uppercase tracking-[0.3em] mb-2">{raffle.category}</p>
            <h3 className="font-display text-2xl sm:text-[1.7rem] font-extrabold text-white leading-[1.05] tracking-tight line-clamp-2 drop-shadow-[0_2px_8px_rgba(0,0,0,0.7)]">
              {raffle.title}
            </h3>
          </div>
        </div>

        {/* Info panel */}
        <div className="p-5 bg-[#040b08] border-t border-[hsl(var(--gold)/0.25)]">
          {isComingSoon ? (
            /* COMING SOON: simple teaser — no countdown, no price, no purchase */
            <div className="text-center py-3">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-500/15 border border-amber-400/40 mb-3">
                <span className="text-2xl">⏳</span>
                <span className="font-display text-sm font-bold text-amber-300 tracking-wide uppercase">
                  <InlineEditable tKey="comingSoon">{t('comingSoon') || 'Bientôt disponible'}</InlineEditable>
                </span>
              </div>
              <p className="text-cream/70 text-xs leading-relaxed">
                <InlineEditable tKey="comingSoonHint" multiline>{t('comingSoonHint') || 'Restez connecté ! Ce tirage sera bientôt disponible.'}</InlineEditable>
              </p>
            </div>
          ) : (
            <>
              {/* Countdown / Sold Out advanced-draw display */}
              {isSoldOut ? (
                hasAdvancedDrawDate ? (
                  /* Sold out + admin set an advanced draw date → countdown */
                  <div className="mb-4" data-testid="raffle-card-advanced-countdown">
                    <CountdownTimer endDate={raffle.draw_date} mode="card" soldOut />
                  </div>
                ) : (
                  /* Sold out but no date yet → "Coming soon" notice */
                  <div className="mb-4 p-3 rounded-xl border-2 border-amber-400/40 bg-gradient-to-br from-amber-500/10 to-red-500/10 text-center" data-testid="raffle-card-advanced-pending">
                    <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-amber-300 mb-1">
                      🎯 <InlineEditable tKey="advancedDrawLabel">{t('advancedDrawLabel') || 'Tirage avancé'}</InlineEditable>
                    </p>
                    <p className="text-sm font-display font-bold text-cream leading-tight">
                      <InlineEditable tKey="advancedDrawComingSoon">{t('advancedDrawComingSoon') || 'Date à venir prochainement'}</InlineEditable>
                    </p>
                  </div>
                )
              ) : (
                /* Big pulsing countdown — normal case */
                raffle.ends_at && (
                  <div className="mb-4" data-testid="raffle-card-countdown">
                    <CountdownTimer endDate={raffle.ends_at} mode="card" soldOut={false} />
                  </div>
                )
              )}

              {/* Big info badges: PRICE + MAX TICKETS (no "remaining" — we never tell the customer how many are left) */}
              <div className="grid grid-cols-2 gap-2 mb-4">
                <div className="rounded-xl border border-[hsl(var(--gold)/0.40)] bg-gold-gradient/10 p-3 text-center">
                  <p className="text-[9px] uppercase tracking-[0.18em] text-gold/95 font-bold mb-1">
                    <Ticket className="w-3 h-3 inline" /> <InlineEditable tKey="ticketPrice">{t('ticketPrice') || 'Prix ticket'}</InlineEditable>
                  </p>
                  <p className="font-mono text-2xl font-black text-gold-gradient leading-none">£{priceGBP}</p>
                </div>
                <div className="rounded-xl border border-cream/15 bg-cream/5 p-3 text-center">
                  <p className="text-[9px] uppercase tracking-[0.18em] text-cream/65 font-bold mb-1">
                    <Users className="w-3 h-3 inline" /> <InlineEditable tKey="ticketsMax">{t('ticketsMax') || 'Tickets max'}</InlineEditable>
                  </p>
                  <p className="font-mono text-2xl font-black text-cream leading-none">{max.toLocaleString()}</p>
                </div>
              </div>

              {/* Action button (no "X left" disclosed) */}
              {isSoldOut ? (
                <div className="px-5 py-3 rounded-lg text-xs font-bold tracking-[0.12em] uppercase bg-red-600/30 border border-red-400/50 text-red-200 cursor-not-allowed text-center">
                  <InlineEditable tKey="soldOut">{t('soldOut') || 'Sold Out'}</InlineEditable>
                </div>
              ) : (
                <div className="btn-gold w-full px-5 py-3 rounded-lg text-xs font-bold tracking-[0.12em] uppercase flex items-center justify-center gap-1.5 group-hover:gap-2.5 transition-all">
                  <InlineEditable tKey="enterDraw">{t('enterDraw')}</InlineEditable>
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Link>
  );
}
